/**
 * Encontra a unidade de um código digitado. Casamento **case-insensitive** com
 * trim; ignora unidades inativas. Retorna `null` quando o código é vazio ou não
 * está cadastrado — "não sei" nunca vira um default silencioso.
 *
 * Puro: recebe a lista já carregada. Nunca faz I/O — o catálogo de unidades é
 * pequeno e já vive em cache (`useEntityRecords`).
 *
 * Se houver mais de uma unidade ativa com o mesmo código, devolve a **primeira**
 * na ordem recebida (determinístico — tipicamente a ordem do `sort` do serviço).
 */
export function resolveUnitByCode(code, units) {
    const normalized = (code ?? '').trim().toLowerCase();
    if (!normalized)
        return null;
    for (const unit of units) {
        if (unit.active === false)
            continue;
        if ((unit.code ?? '').trim().toLowerCase() === normalized)
            return unit;
    }
    return null;
}
/**
 * Reduz um valor **calculado** a `decimals` casas, arredondando **meio para cima
 * em magnitude** (HALF_UP simétrico): 1,005 → 1,01 · -1,005 → -1,01.
 *
 * Aplicar **na borda** — ao gravar —, nunca no meio de uma cadeia de cálculo
 * (rateio, conversão, escalonamento de ficha): arredondar a cada passo acumula
 * erro. Spec 0032 §3.
 *
 * Cuidado com IEEE-754: `1.005` é `1.00499…`, então `Math.round(v*100)/100`
 * devolve 1,00. Implementar por string (dígitos via `toString` de round-trip),
 * como o `truncateDecimals` do DS já faz pelo mesmo motivo.
 */
export function roundQuantity(value, decimals) {
    if (!Number.isFinite(value))
        return value;
    if (!Number.isFinite(decimals))
        return value;
    const precision = Math.max(0, Math.trunc(decimals));
    const negative = value < 0;
    const absStr = Math.abs(value).toString();
    if (absStr.includes('e') || absStr.includes('E')) {
        const factor = 10 ** precision;
        const scaled = Math.abs(value) * factor;
        const rounded = Math.floor(scaled + 0.5);
        const result = rounded / factor;
        return negative ? -result : result;
    }
    const [intPart, fracPart = ''] = absStr.split('.');
    if (precision <= 0) {
        const firstFrac = fracPart.charAt(0);
        let intNum = Number(intPart);
        if (firstFrac !== '' && Number(firstFrac) >= 5)
            intNum += 1;
        return negative ? -intNum : intNum;
    }
    const keep = fracPart.slice(0, precision);
    const nextDigit = fracPart.charAt(precision);
    let intNum = Number(intPart);
    let fracDigits = keep.padEnd(precision, '0');
    if (nextDigit !== '' && Number(nextDigit) >= 5) {
        const fracAsInt = Number(fracDigits) + 1;
        const maxFrac = 10 ** precision;
        if (fracAsInt >= maxFrac) {
            intNum += 1;
            fracDigits = '0'.repeat(precision);
        }
        else {
            fracDigits = String(fracAsInt).padStart(precision, '0');
        }
    }
    const normalized = Number(`${intNum}.${fracDigits}`);
    return negative ? -normalized : normalized;
}
/**
 * Truncates a value to `decimals` places toward zero (no rounding up).
 * Same string path as {@link roundQuantity} so IEEE-754 does not steal a unit
 * (`0.29 * 100 → 28.999…`). Used by largest-remainder apportionment.
 */
export function floorQuantity(value, decimals) {
    if (!Number.isFinite(value))
        return value;
    if (!Number.isFinite(decimals))
        return value;
    const precision = Math.max(0, Math.trunc(decimals));
    const negative = value < 0;
    const absStr = Math.abs(value).toString();
    if (absStr.includes('e') || absStr.includes('E')) {
        const factor = 10 ** precision;
        const floored = Math.floor(Math.abs(value) * factor + 1e-10);
        const result = floored / factor;
        return negative ? -result : result;
    }
    const [intPart, fracPart = ''] = absStr.split('.');
    if (precision <= 0) {
        const intNum = Number(intPart);
        return negative ? -intNum : intNum;
    }
    const fracDigits = fracPart.slice(0, precision).padEnd(precision, '0');
    const normalized = Number(`${intPart}.${fracDigits}`);
    return negative ? -normalized : normalized;
}
//# sourceMappingURL=quantity.js.map